/**
 * The soundtrack as data. A vibe is everything the music scheduler needs
 * to play a level's mood: tempo, root, a 32-step bassline, stab chords,
 * an acid line, and the intensity thresholds that gate each layer in as
 * the player builds speed. Levels select a vibe by id (a level def can
 * also carry `music: { vibe }` for the editor future); sectors that share
 * a vibe still differ by transpose.
 *
 * Notes are semitones relative to the vibe root; root is semitones
 * relative to A55. Basslines use -1 as a rest.
 */

export const VIBES = {
  // The flagship: driving electro in the Dog Blood lane. F minor, 128.
  surge: {
    bpm: 128,
    root: -4,
    bass: [
      -1, -1, 0, 0, -1, -1, 0, 3, -1, -1, 0, 0, -1, 7, 5, 3,
      -1, -1, 0, 0, -1, -1, 3, 5, -1, -1, 8, 7, -1, 5, 3, 2,
    ],
    stabs: { 3: [0, 3, 7], 11: [8, 12, 15], 19: [0, 3, 7], 26: [10, 14, 17], 30: [7, 10, 14] },
    acid: [0, 3, 7, 10, 12, 10, 7, 3],
    clap: false,
    pads: false,
    kick: { f0: 155, f1: 42, decay: 0.26, peak: 0.62 },
    sub: 0.55,
    thresholds: { bass: 0.08, hats16: 0.4, stabs: 0.35, acid: 0.72 },
  },
  // First steps: the same world, half asleep. Sparse, padded, patient.
  ignition: {
    bpm: 120,
    root: -4,
    bass: [
      0, -1, -1, -1, -1, -1, -1, -1, 0, -1, -1, -1, -1, -1, 3, -1,
      0, -1, -1, -1, -1, -1, -1, -1, -2, -1, -1, -1, -1, -1, -1, -1,
    ],
    stabs: {},
    acid: null,
    clap: false,
    pads: true,
    kick: { f0: 130, f1: 40, decay: 0.3, peak: 0.42 },
    sub: 0.4,
    thresholds: { bass: 0.0, hats16: 0.6, stabs: 2, acid: 2 },
  },
  // Rings and rebounds: bouncy, brighter, offbeat bass, claps.
  bloom: {
    bpm: 128,
    root: 3,
    bass: [
      -1, -1, 0, -1, -1, -1, 7, -1, -1, -1, 0, -1, -1, -1, 10, 7,
      -1, -1, 0, -1, -1, -1, 5, -1, -1, -1, 3, -1, -1, 7, 5, 3,
    ],
    stabs: { 6: [0, 3, 7], 14: [5, 8, 12], 22: [0, 3, 7], 29: [10, 14, 17] },
    acid: [0, 3, 5, 7, 10, 7, 5, 3],
    clap: true,
    pads: false,
    kick: { f0: 150, f1: 45, decay: 0.24, peak: 0.55 },
    sub: 0.45,
    thresholds: { bass: 0.06, hats16: 0.35, stabs: 0.3, acid: 0.7 },
  },
  // Jetstreams and light: half-time float, pads forward, air instead of acid.
  drift: {
    bpm: 100,
    root: 1,
    bass: [
      0, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, 3, -1, -1, -1,
      -2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, 5, -1, 3, -1,
    ],
    stabs: {},
    acid: null,
    clap: false,
    pads: true,
    halfTime: true,
    kick: { f0: 120, f1: 38, decay: 0.34, peak: 0.4 },
    sub: 0.6,
    thresholds: { bass: 0.0, hats16: 0.55, stabs: 2, acid: 2 },
  },
  // Towers and pits: heavier sub, darker stabs, the acid arrives late.
  depth: {
    bpm: 124,
    root: 0,
    bass: [
      0, -1, -1, 0, -1, -1, 0, -1, -1, 0, -1, -1, 0, -1, 3, -1,
      0, -1, -1, 0, -1, -1, 0, -1, -1, 0, -1, -1, 8, -1, 7, 5,
    ],
    stabs: { 7: [0, 3, 7], 23: [8, 12, 15] },
    acid: [0, 0, 3, 7, 0, 10, 7, 3],
    clap: false,
    pads: true,
    kick: { f0: 145, f1: 40, decay: 0.28, peak: 0.58 },
    sub: 0.65,
    thresholds: { bass: 0.05, hats16: 0.45, stabs: 0.45, acid: 0.8 },
  },
  // One life: surge's meaner sibling, a step darker and a hair slower.
  onelife: {
    bpm: 126,
    root: -6,
    bass: [
      -1, -1, 0, 0, -1, -1, 0, 1, -1, -1, 0, 0, -1, 6, 5, 1,
      -1, -1, 0, 0, -1, -1, 1, 5, -1, -1, 6, 5, -1, 3, 1, 0,
    ],
    stabs: { 3: [0, 1, 7], 11: [6, 10, 13], 19: [0, 1, 7], 27: [5, 8, 12] },
    acid: [0, 1, 5, 6, 12, 6, 5, 1],
    clap: false,
    pads: false,
    kick: { f0: 150, f1: 40, decay: 0.28, peak: 0.62 },
    sub: 0.6,
    thresholds: { bass: 0.08, hats16: 0.4, stabs: 0.35, acid: 0.68 },
  },
};

export const LEVEL_VIBES = {
  boot: 'ignition',
  freefall: 'surge',
  uplink: 'depth',
  spire: 'depth',
  velocity: 'surge',
  descent: 'depth',
  cascade: 'bloom',
  turbine: 'surge',
  rebound: 'bloom',
  aurora: 'drift',
  zephyr: 'drift',
  slipstream: 'drift',
  conflux: 'surge',
  fracture: 'surge',
  foundry: 'ignition',
  piston: 'depth',
  watchtower: 'onelife',
  phosphor: 'drift',
  skybreak: 'bloom',
  switchback: 'depth',
  crossflow: 'surge', relay: 'depth',
  gauntlet: 'onelife',
};

export function vibeForLevel(def) {
  const name = def?.music?.vibe ?? LEVEL_VIBES[def?.id] ?? 'surge';
  return { name, ...(VIBES[name] ?? VIBES.surge) };
}
